import { motion } from 'framer-motion'

interface Props {
  value: number
  label: string
  
  secondaryValue?: number
  secondaryLabel?: string
  size?: number
}

export function NpsGauge({ value, label, secondaryValue, secondaryLabel, size = 180 }: Props) {
  const radius = size / 2 - 14
  const cx = size / 2
  const cy = size / 2 + 8
  const startAngle = Math.PI
  const endAngle = 2 * Math.PI

  const v = clamp(value, -100, 100)
  const t = (v + 100) / 200

  const arc = describeArc(cx, cy, radius, startAngle, endAngle)
  const filledEnd = startAngle + (endAngle - startAngle) * t
  const filledArc = describeArc(cx, cy, radius, startAngle, filledEnd)

  return (
    <div className="flex items-center gap-6">
      <svg width={size} height={size / 1.5} viewBox={`0 0 ${size} ${size / 1.5}`}>
        <path
          d={arc}
          stroke="var(--border-strong)"
          strokeWidth={10}
          fill="none"
          strokeLinecap="round"
        />
        <motion.path
          d={filledArc}
          stroke="var(--brand-primary)"
          strokeWidth={10}
          fill="none"
          strokeLinecap="round"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
        />
        <text
          x={cx}
          y={cy - 4}
          textAnchor="middle"
          className="fill-ink font-display"
          style={{ fontSize: size / 4, fontWeight: 700 }}
        >
          {Math.round(value)}
        </text>
      </svg>
      <div className="space-y-1">
        <div className="text-xs uppercase tracking-widest text-ink-subtle">{label}</div>
        {secondaryValue !== undefined && (
          <div className="space-y-0.5">
            <div className="font-display text-2xl font-bold text-ink">
              {secondaryValue.toFixed(2)}
            </div>
            <div className="text-xs uppercase tracking-widest text-ink-subtle">
              {secondaryLabel}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n))
}

function describeArc(cx: number, cy: number, r: number, start: number, end: number) {
  const x1 = cx + r * Math.cos(start)
  const y1 = cy + r * Math.sin(start)
  const x2 = cx + r * Math.cos(end)
  const y2 = cy + r * Math.sin(end)
  const large = end - start > Math.PI ? 1 : 0
  return `M ${x1.toFixed(2)} ${y1.toFixed(2)} A ${r} ${r} 0 ${large} 1 ${x2.toFixed(2)} ${y2.toFixed(2)}`
}
